// @ts-nocheck
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { loadUserRoles, loadOrgMemberships } from '@/lib/authQueries';

type RoleState = {
  loading: boolean;
  roles: string[];
  isSuperAdmin: boolean;
  isTenantAdmin: boolean;
  isTenantStaff: boolean;
  orgIds: string[];
  /** True when a lookup failed — NOT the same as "has no roles". */
  unknown: boolean;
  refresh: () => Promise<void>;
};

const RoleContext = createContext<RoleState>({
  loading: true,
  roles: [],
  isSuperAdmin: false,
  isTenantAdmin: false,
  isTenantStaff: false,
  orgIds: [],
  unknown: false,
  refresh: async () => {},
});

export const RoleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [orgIds, setOrgIds] = useState<string[]>([]);
  const [unknown, setUnknown] = useState(false);

  const fetchRoles = useCallback(async (force = false) => {
    if (!user) {
      setRoles([]);
      setOrgIds([]);
      setUnknown(false);
      setLoading(false);
      return;
    }
    setLoading(true);

    // Shared, cached, retried reads — one per session, not one per second.
    const [rolesOut, orgOut] = await Promise.all([
      loadUserRoles(user.id, force),
      loadOrgMemberships(user.id, force),
    ]);

    if (rolesOut.status === 'ok') setRoles(rolesOut.data);
    if (orgOut.status === 'ok') setOrgIds(orgOut.data);
    // A failure leaves whatever we already knew in place.
    setUnknown(rolesOut.status !== 'ok' || orgOut.status !== 'ok');
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const value: RoleState = React.useMemo(
    () => ({
      loading,
      roles,
      orgIds,
      unknown,
      isSuperAdmin: roles.includes('rlevents_super_admin') || roles.includes('super_admin'),
      isTenantAdmin: roles.includes('rlevents_tenant_admin'),
      isTenantStaff: roles.includes('rlevents_tenant_staff'),
      refresh: () => fetchRoles(true),
    }),
    [loading, roles, orgIds, unknown, fetchRoles]
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
};

export const useRole = () => useContext(RoleContext);
